import React, { useState } from "react";
import "../styles/Partnership.css";

const Partnership = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call - replace with actual API endpoint
      await simulateApiCall(formData);
      
      // Show success state
      setSubmitted(true);
      
      // Optional: Reset form data
      // setFormData({ name: "", email: "", organization: "", message: "" });
      
    } catch (error) {
      console.error("Submission failed:", error);
      // Handle error state here if needed
      alert("Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Simulate API call with random delay
  const simulateApiCall = (data) => {
    return new Promise((resolve, reject) => {
      // Simulate network delay
      const delay = Math.random() * 2000 + 1000; // 1-3 seconds
      
      setTimeout(() => {
        // Simulate 95% success rate
        if (Math.random() < 0.95) {
          console.log("Partnership request submitted:", data);
          console.log("Timestamp:", new Date().toISOString());
          resolve({ success: true, message: "Partnership request received" });
        } else {
          reject(new Error("Network error"));
        }
      }, delay);
    });
  };

  const resetForm = () => {
    setSubmitted(false);
    setFormData({
      name: "",
      email: "",
      organization: "",
      message: "",
    });
    setErrors({});
  };

  return (
    <div className="partnership-page">
      <div className="partnership-container">
        {/* Header Section */}
        <div className="partnership-header">
          <div className="header-icon">
            <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h1 className="partnership-title">Partner With Us</h1>
        </div>

        {/* Form Container */}
        <div className="form-container">
          {submitted ? (
            <div className="success-container">
              {/* Success Animation */}
              <div className="success-icon">
                <svg className="check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h3 className="success-title">Thank You!</h3>
              <div className="success-message">
                <p>Your partnership request has been received successfully.</p>
                <p className="highlight">We'll get back to you as soon as possible!</p>
                <div className="submitted-info">
                  <p><strong>Submitted:</strong> {new Date().toLocaleString()}</p>
                  <p><strong>Reference ID:</strong> PTR-{Date.now().toString().slice(-6)}</p>
                </div>
              </div>
              
              <button onClick={resetForm} className="reset-btn">
                <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Submit Another Request
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="partnership-form">
              {/* Name Field */}
              <div className="form-group">
                <label className="form-label">
                  Full Name <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-input ${errors.name ? 'error' : ''}`}
                    required
                  />
                  <div className="input-icon">
                    <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              {/* Email Field */}
              <div className="form-group">
                <label className="form-label">
                  Email Address <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-input ${errors.email ? 'error' : ''}`}
                    required
                  />
                  <div className="input-icon">
                    <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              {/* Organization Field */}
              <div className="form-group">
                <label className="form-label">Organization</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="organization"
                    placeholder="Enter your organization name"
                    value={formData.organization}
                    onChange={handleChange}
                    className="form-input"
                  />
                  <div className="input-icon">
                    <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Message Field */}
              <div className="form-group">
                <label className="form-label">
                  Partnership Message <span className="required">*</span>
                </label>
                <textarea
                  name="message"
                  placeholder="Tell us more about your partnership idea and how we can collaborate..."
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  className={`form-textarea ${errors.message ? 'error' : ''}`}
                  required
                />
                {errors.message && <span className="error-message">{errors.message}</span>}
                <div className="character-count">
                  {formData.message.length}/500 characters
                </div>
              </div>

              {/* Submit Button */}
              <div className="submit-section">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
                >
                  <span className={`btn-content ${isSubmitting ? 'hidden' : ''}`}>
                    <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    Submit Partnership Request
                  </span>
                  
                  {/* Loading Animation */}
                  <div className={`loading-content ${isSubmitting ? 'visible' : ''}`}>
                    <div className="spinner"></div>
                    <span>Processing Request...</span>
                  </div>
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Trust Indicators */}
        <div className="trust-indicators">
          <div className="indicator">
            <svg className="indicator-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>Secure & Confidential</span>
          </div>
          <div className="indicator">
            <svg className="indicator-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>Quick Response</span>
          </div>
          <div className="indicator">
            <svg className="indicator-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Verified Process</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Partnership;